import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { 
  Search, 
  X, 
  Package, 
  ChevronDown, 
  Plus,
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  Upload,
  Download,
  Boxes,
  AlertTriangle,
  TrendingUp,
  ArrowUpDown,
  Loader2,
  AlertCircle,
  Eye,
  Calendar,
  DollarSign,
  Percent,
  CheckCircle,
  PackageX,
  PackageMinus,
  CalendarClock,
  TrendingDown,
  Trash2,
  ShieldAlert,
  Users,
  Image,
  MapPin,
  SlidersHorizontal,
  ArrowLeftRight
} from 'lucide-react';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/react';
import ExcelJS from 'exceljs';
import toast from 'react-hot-toast';
import { SuppliersService } from '../services/suppliers/suppliers.service';
import { ProductsService, type Product } from '../services/products/products.service';
import { UsersService } from '../services/users/users.service';
import { useProductDrawerStore } from '../store/productDrawer.store';
import { BulkUploadDrawer } from '../components/products/BulkUploadDrawer';
import { ProductDetailsDrawer } from '../components/products/ProductDetailsDrawer';
import { AddLoteDrawer } from '../components/products/AddLoteDrawer';
import { usePermissions } from '../hooks/usePermissions';
import { getFirstProductImage } from '../utils/product-images';
import { capitalizeProductName } from '../utils/capitalize';

interface PaginationMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

interface PaginatedProductsResponse {
  data: Product[];
  meta: PaginationMeta;
}

export default function ProductosGestionPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasPermission, isSuperAdmin, isVendedor, user } = usePermissions();
  

  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(7);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const userDropdownRef = useRef<HTMLDivElement>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState<'list' | 'stats'>('list');
  const [creatorFilter, setCreatorFilter] = useState('');
  const [ubicacionFilter, setUbicacionFilter] = useState('');
  const [isUbicacionDropdownOpen, setIsUbicacionDropdownOpen] = useState(false);
  const [ubicacionSearchTerm, setUbicacionSearchTerm] = useState('');
  const ubicacionDropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Filtro de estado (activo/inactivo)
  const [estadoFilter, setEstadoFilter] = useState<'todos' | 'activo' | 'inactivo'>('todos');

  // Filtro de stock
  const [stockFilter, setStockFilter] = useState<'todos' | 'agotado' | 'bajo' | 'disponible'>('todos');

  // Filter drawer
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  // Active filters count
  const activeFiltersCount = [
    creatorFilter,
    ubicacionFilter,
    estadoFilter !== 'todos',
    stockFilter !== 'todos',
  ].filter(Boolean).length;

  // Modal dar de baja
  const [isBajaModalOpen, setIsBajaModalOpen] = useState(false);
  const [bajaProducto, setBajaProducto] = useState<Product | null>(null);
  const [bajaMotivo, setBajaMotivo] = useState('VENCIMIENTO');
  const [bajaCantidad, setBajaCantidad] = useState('');
  const [bajaObservaciones, setBajaObservaciones] = useState('');
  const [bajaLoteId, setBajaLoteId] = useState('');
  const [bajaPresentacionId, setBajaPresentacionId] = useState('');
  const [isBajaSubmitting, setIsBajaSubmitting] = useState(false);

  const deactivateMutation = useMutation({
    mutationFn: async (data: { productId: string; motivo: string; cantidad: number; observaciones?: string; loteId?: string; presentacionId?: string }) => {
      return ProductsService.darDeBaja(data.productId, {
        motivo: data.motivo,
        cantidad: data.cantidad,
        observaciones: data.observaciones,
        loteId: data.loteId,
        presentacionId: data.presentacionId,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-products'] });
      toast.success(bajaLoteId ? 'Lote dado de baja correctamente' : 'Producto dado de baja correctamente');
      setIsBajaModalOpen(false);
      setBajaProducto(null);
      setBajaMotivo('VENCIMIENTO');
      setBajaCantidad('');
      setBajaObservaciones('');
      setBajaLoteId('');
      setBajaPresentacionId('');
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || error?.message || 'Error al dar de baja');
    },
  });
  
  const { openCreate, openEdit, close } = useProductDrawerStore();
  const [isBulkDrawerOpen, setIsBulkDrawerOpen] = useState(false);
  const [isDetailsDrawerOpen, setIsDetailsDrawerOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [productForLote, setProductForLote] = useState<Product | null>(null);
  const [isLoteDrawerOpen, setIsLoteDrawerOpen] = useState(false);

  // Estados para expandir/colapsar alertas del dashboard
  const [showAllOutOfStock, setShowAllOutOfStock] = useState(false);
  const [showAllLowStock, setShowAllLowStock] = useState(false);
  const [showAllExpiring, setShowAllExpiring] = useState(false);
  const [showAllLowMargin, setShowAllLowMargin] = useState(false);
  const [diasVencimientoFiltro, setDiasVencimientoFiltro] = useState(120);

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 200);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Reset page when search or creator filter changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm, creatorFilter, ubicacionFilter, estadoFilter, stockFilter]);

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (isDropdownOpen && !target.closest('[data-dropdown]')) {
        setIsDropdownOpen(false);
      }
      if (isUserDropdownOpen && userDropdownRef.current && !userDropdownRef.current.contains(target)) {
        setIsUserDropdownOpen(false);
        setUserSearchTerm('');
      }
      if (isUbicacionDropdownOpen && ubicacionDropdownRef.current && !ubicacionDropdownRef.current.contains(target)) {
        setIsUbicacionDropdownOpen(false);
        setUbicacionSearchTerm('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen, isUserDropdownOpen, isUbicacionDropdownOpen]);

  // Query users for creator filter
  const { data: usersData } = useQuery({
    queryKey: ['users-select'],
    queryFn: async () => {
      const response = await UsersService.getUsers(1, 500);
      return response?.data || [];
    },
  });

  // Query products (paginated for table)
  const {
    data,
    isLoading,
    error,
    refetch,
    isFetching
  } = useQuery<PaginatedProductsResponse>({
    queryKey: ['admin-products', page, limit, debouncedSearchTerm, creatorFilter, ubicacionFilter, estadoFilter, stockFilter],
    queryFn: async () => {
      const activo = estadoFilter === 'todos' ? undefined : estadoFilter === 'activo';
      const stockStatus = stockFilter === 'todos' ? undefined : stockFilter;
      const response = await ProductsService.list(page, limit, debouncedSearchTerm, undefined, creatorFilter, ubicacionFilter, activo, stockStatus);
      return response.data;
    },
    placeholderData: (previousData) => previousData,
  });

  // Query ALL products for dashboard stats and location filter (no pagination)
  const { data: allProductsData } = useQuery<{ data: Product[] }>({
    queryKey: ['admin-products-all-stats'],
    queryFn: async () => {
      const response = await ProductsService.list(1, 1000); // Get up to 1000 products
      return response.data;
    },
  });

  const products = data?.data || [];
  const paginationMeta = data?.meta;
  const allProducts = allProductsData?.data || [];

  // Extract unique locations from all products and sort: Vitrinas → Estanterías → Farmaly → resto
  const ubicacionesUnicas = Array.from(
    new Set(allProducts.map((p: Product) => p.ubicacion).filter((u): u is string => !!u))
  ).sort((a: string, b: string) => {
    const getPriority = (u: string) => {
      const lower = u.toLowerCase();
      if (lower.includes('vitrina')) return 1;
      if (lower.includes('estanteria') || lower.includes('estantería')) return 2;
      if (lower.includes('farmaly')) return 3;
      return 4;
    };
    const getNumber = (u: string) => {
      const match = u.match(/\d+/);
      return match ? parseInt(match[0], 10) : 0;
    };
    const pa = getPriority(a);
    const pb = getPriority(b);
    if (pa !== pb) return pa - pb;
    // Same priority: sort by extracted number
    return getNumber(a) - getNumber(b);
  }) as string[];

  // Calculate stats from ALL products
  const stats = {
    total: paginationMeta?.total || 0,
    lowStock: allProducts.filter((p: Product) => {
      const s = p.presentaciones?.[0]?.stock || 0;
      return s <= 5 && s > 0;
    }).length,
    outOfStock: allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) === 0).length,
    totalValue: allProducts.reduce((acc: number, p: Product) => acc + ((p.presentaciones?.[0]?.precio || 0) * (p.presentaciones?.[0]?.stock || 0)), 0),
    totalCost: allProducts.reduce((acc: number, p: Product) => acc + (((p.presentaciones?.[0]?.precioCompra || 0)) * (p.presentaciones?.[0]?.stock || 0)), 0),
    totalProfit: allProducts.reduce((acc: number, p: Product) => acc + (((p.presentaciones?.[0]?.precio || 0) - (p.presentaciones?.[0]?.precioCompra || 0)) * (p.presentaciones?.[0]?.stock || 0)), 0),
    valueByPresentation: allProducts.reduce((acc: Record<string, number>, p: Product) => {
      p.presentaciones?.forEach((pres) => {
        const nombre = pres.nombre || 'Sin nombre';
        const subtotal = (pres.precio || 0) * (pres.stock || 0);
        acc[nombre] = (acc[nombre] || 0) + subtotal;
      });
      return acc;
    }, {} as Record<string, number>),
  };

  // Focus search input on mount (con delay para asegurar que el DOM y otros elementos estén listos)
  useEffect(() => {
    const timer = setTimeout(() => {
      searchInputRef.current?.focus();
      searchInputRef.current?.select();
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  // Keep focus on search input after fetching
  useEffect(() => {
    if (!isFetching && searchTerm && searchInputRef.current) {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 100);
    }
  }, [isFetching, searchTerm]);

  const handleProductClick = (product: Product) => {
    setSelectedProduct(product);
    setIsDetailsDrawerOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsDrawerOpen(false);
    setSelectedProduct(null);
  };

  const handleEdit = (product: Product) => {
    openEdit(product);
  };

  const handleAddLote = (product: Product) => {
    setProductForLote(product);
    setIsLoteDrawerOpen(true);
  };

  const handleCloseLoteDrawer = () => {
    setIsLoteDrawerOpen(false);
    setProductForLote(null);
  };

  const handleOpenBajaModal = (product: Product, preselectedLoteId?: string) => {
    setBajaProducto(product);
    setBajaMotivo('VENCIMIENTO');
    setBajaObservaciones('');
    setBajaLoteId(preselectedLoteId || '');
    setBajaPresentacionId('');
    setBajaCantidad('');
    if (preselectedLoteId && product.lotes) {
      const lote = product.lotes.find(l => l.id === preselectedLoteId);
      if (lote) {
        setBajaCantidad(lote.cantidad.toString());
        setBajaPresentacionId(lote.presentacionId);
      }
    }
    setIsBajaModalOpen(true);
  };

  const handleBajaSubmit = async () => {
    if (!bajaProducto) return;
    const cantidad = parseInt(bajaCantidad, 10);
    if (isNaN(cantidad) || cantidad <= 0) {
      toast.error('Ingrese una cantidad válida');
      return;
    }
    if (bajaLoteId) {
      const lote = bajaProducto.lotes?.find(l => l.id === bajaLoteId);
      if (lote && cantidad > lote.cantidad) {
        toast.error(`La cantidad no puede superar el stock del lote (${lote.cantidad})`);
        return;
      }
    }
    setIsBajaSubmitting(true);
    try {
      await deactivateMutation.mutateAsync({
        productId: bajaProducto.id,
        motivo: bajaMotivo,
        cantidad,
        observaciones: bajaObservaciones || undefined,
        loteId: bajaLoteId || undefined,
        presentacionId: bajaPresentacionId || undefined,
      });
    } finally {
      setIsBajaSubmitting(false);
    }
  };

  const handleCloseCreateDrawer = () => {
    close();
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePreviousPage = () => {
    if (paginationMeta?.hasPreviousPage) {
      setPage(page - 1);
    }
  };

  const handleNextPage = () => {
    if (paginationMeta?.hasNextPage) {
      setPage(page + 1);
    }
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleSearchChange = (newSearchTerm: string) => {
    setSearchTerm(newSearchTerm);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  const getStockStatus = (stock: number, minStock: number = 5) => {
    if (stock === 0) return { color: 'red', label: 'Agotado', bg: 'bg-red-100', text: 'text-red-800', dot: 'bg-red-500' };
    if (stock <= minStock) return { color: 'amber', label: 'Bajo stock', bg: 'bg-amber-100', text: 'text-amber-800', dot: 'bg-amber-500' };
    return { color: 'emerald', label: 'Disponible', bg: 'bg-emerald-100', text: 'text-emerald-800', dot: 'bg-emerald-500' };
  };

  const getProximaFechaVencimiento = (product: Product): string | null => {
    if (!product.lotes || product.lotes.length === 0) return null;
    const fechas = product.lotes
      .filter(l => l.fechaVencimiento)
      .map(l => new Date(l.fechaVencimiento!))
      .sort((a, b) => a.getTime() - b.getTime());
    return fechas.length > 0 ? fechas[0].toISOString() : null;
  };

  const getDiasRestantesVencimiento = (product: Product): number | null => {
    const fecha = getProximaFechaVencimiento(product);
    if (!fecha) return null;
    return Math.ceil((new Date(fecha).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
  };

  const getProductosProximosAVencer = (products: Product[], diasLimite: number) => {
    return products.filter((p: Product) => {
      const fechaVenc = getProximaFechaVencimiento(p);
      if (!fechaVenc) return false;
      const dias = Math.ceil((new Date(fechaVenc).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
      return dias >= 0 && dias <= diasLimite;
    });
  };

  const getVencimientoStatus = (dias: number | null) => {
    if (dias === null) return { label: 'Sin lote', bg: 'bg-gray-100', text: 'text-gray-500', border: 'border-gray-200', icon: null, urgent: false };
    if (dias < 0) return { label: `Vencido (${Math.abs(dias)}d)`, bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: 'alert', urgent: true };
    if (dias === 0) return { label: 'Vence hoy', bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', icon: 'alert', urgent: true };
    if (dias <= 7) return { label: `${dias} días`, bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-200', icon: 'alert', urgent: true };
    if (dias <= 30) return { label: `${dias} días`, bg: 'bg-amber-50', text: 'text-amber-700', border: 'border-amber-200', icon: 'clock', urgent: true };
    if (dias <= 90) return { label: `${dias} días`, bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-200', icon: 'calendar', urgent: false };
    return { label: `${dias} días`, bg: 'bg-emerald-50', text: 'text-emerald-700', border: 'border-emerald-200', icon: 'calendar', urgent: false };
  };

  const handleExportInventory = async () => {
    try {
      const [productsResponse, suppliersResponse] = await Promise.all([
        ProductsService.list(1, 10000),
        SuppliersService.getAllForSelect(),
      ]);
      const allProducts = productsResponse.data?.data || [];
      const supplierMap = new Map((suppliersResponse.data || []).map((s: any) => [s.id, s.nombre]));
      if (allProducts.length === 0) {
        alert('No hay productos para exportar');
        return;
      }

      const workbook = new ExcelJS.Workbook();
      workbook.creator = 'Confirma Amiga';
      workbook.created = new Date();

      // Agrupar por ubicación
      const grouped: Record<string, Product[]> = {};
      allProducts.forEach((p: Product) => {
        const ubicacion = p.ubicacion || 'Sin ubicación';
        if (!grouped[ubicacion]) grouped[ubicacion] = [];
        grouped[ubicacion].push(p);
      });

      Object.entries(grouped).forEach(([ubicacion, products]) => {
        const sheetName = ubicacion.slice(0, 31);
        const ws = workbook.addWorksheet(sheetName);

        // Título de la hoja
        const isSuper = isSuperAdmin();
        const lastCol = isSuper ? 'U' : 'R';
        ws.mergeCells(`A1:${lastCol}1`);
        const titleCell = ws.getCell('A1');
        titleCell.value = `Inventario - ${ubicacion}`;
        titleCell.font = { size: 16, bold: true, color: { argb: 'FFFFFFFF' } };
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF059669' } };
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
        ws.getRow(1).height = 30;

        // Encabezados
        const headers = [
          'Nombre', 'Categoría', 'Código de barras', 'Laboratorio', 'Principio activo',
          'Registro INVIMA', 'Requiere receta', 'Presentación principal', 'Factor',
          'Precio venta', 'Costo', 'Stock total', 'Presentaciones',
          'Próxima fecha vencimiento', 'Días restantes', 'Estado vencimiento', 'Proveedor', 'Activo',
          ...(isSuper ? ['Margen %', 'Ganancia Total', 'Valor Venta'] : [])
        ];
        const headerRow = ws.addRow(headers);
        headerRow.height = 22;
        headerRow.eachCell((cell) => {
          cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF10B981' } };
          cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF047857' } },
            bottom: { style: 'thin', color: { argb: 'FF047857' } },
            left: { style: 'thin', color: { argb: 'FF047857' } },
            right: { style: 'thin', color: { argb: 'FF047857' } }
          };
        });

        // Filtros y paneles congelados
        ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: headers.length } };
        ws.views = [{ state: 'frozen', xSplit: 0, ySplit: 2 }];

        // Datos
        products.forEach((p: Product) => {
          const principal = p.presentaciones?.find(pr => pr.esPrincipal) || p.presentaciones?.[0];
          const proximaFecha = getProximaFechaVencimiento(p);
          const diasRestantes = getDiasRestantesVencimiento(p);
          const presentacionesStr = p.presentaciones?.map(pr =>
            `${pr.nombre} (${pr.factor}un) - Venta: $${pr.precio.toLocaleString('es-CO')}${pr.precioCompra ? ` / Compra: $${pr.precioCompra.toLocaleString('es-CO')}` : ''}`
          ).join(' | ') || '';

          const estadoVenc = diasRestantes === null ? 'Sin lote' : diasRestantes < 0 ? 'Vencido' : diasRestantes <= 30 ? 'Por vencer' : 'OK';

          const precio = principal?.precio || 0;
          const precioCompra = principal?.precioCompra || 0;
          const stockUnidades = Math.floor((p.stockBase || 0) / (principal?.factor || 1));
          const margen = precioCompra > 0 ? ((precio - precioCompra) / precioCompra) * 100 : 0;
          const gananciaTotal = precioCompra > 0 ? (precio - precioCompra) * stockUnidades : 0;
          const valorVenta = precio * stockUnidades;

          const row = ws.addRow([
            p.nombre,
            p.categoria || '',
            p.codigoBarras || '',
            p.laboratorio || '',
            p.principioActivo || '',
            p.registroInvima || '',
            p.requiereReceta ? 'Sí' : 'No',
            principal?.nombre || '',
            principal?.factor || '',
            precio,
            precioCompra,
            p.stockBase || 0,
            presentacionesStr,
            proximaFecha ? new Date(proximaFecha).toLocaleDateString('es-CO') : 'Sin fecha',
            diasRestantes !== null ? diasRestantes : 'N/A',
            estadoVenc,
            p.lotes?.[0]?.proveedorId ? supplierMap.get(p.lotes[0].proveedorId) || '—' : '—',
            p.activo ? 'Sí' : 'No',
            ...(isSuper ? [margen, gananciaTotal, valorVenta] : [])
          ]);

          row.eachCell((cell, colNumber) => {
            cell.border = {
              top: { style: 'thin', color: { argb: 'FFD1D5DB' } },
              bottom: { style: 'thin', color: { argb: 'FFD1D5DB' } },
              left: { style: 'thin', color: { argb: 'FFD1D5DB' } },
              right: { style: 'thin', color: { argb: 'FFD1D5DB' } }
            };
            cell.alignment = { vertical: 'middle', wrapText: true };

            // Color de fondo según estado de vencimiento
            if (estadoVenc === 'Vencido') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFECACA' } };
            } else if (estadoVenc === 'Por vencer') {
              cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFFEF3C7' } };
            }

            // Formato de moneda para precios
            if (colNumber === 10 || colNumber === 11) {
              cell.numFmt = '$#,##0';
              cell.alignment = { horizontal: 'right', vertical: 'middle' };
            }
            // Centrar números pequeños
            if (colNumber === 9 || colNumber === 12 || colNumber === 15) {
              cell.alignment = { horizontal: 'center', vertical: 'middle' };
            }
            // Formato para columnas de super admin
            if (isSuper) {
              if (colNumber === headers.length - 2 || colNumber === headers.length - 1 || colNumber === headers.length) {
                cell.numFmt = '$#,##0';
                cell.alignment = { horizontal: 'right', vertical: 'middle' };
              }
              if (colNumber === headers.length - 2) {
                cell.numFmt = '0.0"%"';
              }
            }
            // Color para estado vencimiento
            if (colNumber === 17) {
              if (estadoVenc === 'Vencido') {
                cell.font = { bold: true, color: { argb: 'FF991B1B' } };
              } else if (estadoVenc === 'Por vencer') {
                cell.font = { bold: true, color: { argb: 'FF92400E' } };
              } else if (estadoVenc === 'OK') {
                cell.font = { bold: true, color: { argb: 'FF047857' } };
              }
            }
          });
        });

        // Anchos de columna
        ws.columns = [
          { width: 35 }, { width: 14 }, { width: 16 }, { width: 22 }, { width: 22 },
          { width: 16 }, { width: 11 }, { width: 14 }, { width: 8 }, { width: 13 },
          { width: 13 }, { width: 11 }, { width: 45 }, { width: 20 }, { width: 13 },
          { width: 16 }, { width: 20 }, { width: 9 },
          ...(isSuper ? [{ width: 12 }, { width: 14 }, { width: 14 }] : [])
        ];
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      const dateStr = new Date().toISOString().split('T')[0];
      a.href = url;
      a.download = `Inventario_Farmacia_${dateStr}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      alert('Error al exportar: ' + (err.message || 'Error desconocido'));
    }
  };

  if (!user || !hasPermission('Productos', 'ver')) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-emerald-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">Error al cargar los productos</div>
          <button 
            onClick={() => refetch()}
            className="bg-emerald-600 text-white px-4 py-2 rounded-lg hover:bg-emerald-700"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 h-[calc(100vh-80px)] flex flex-col">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Gestión de Productos</h1>
            <p className="text-gray-600 mt-1">
              Administra el inventario de productos de la farmacia
            </p>
          </div>
        </div>
        
        {/* Tabs */}
        <div className="flex space-x-8 mb-6">
          <button
            onClick={() => setActiveTab('list')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'list'
                ? 'text-emerald-700 border-emerald-700'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Lista de Productos
          </button>
          <button
            onClick={() => setActiveTab('stats')}
            className={`py-2 px-1 text-sm font-medium border-b-2 transition-colors ${
              activeTab === 'stats'
                ? 'text-emerald-700 border-emerald-700'
                : 'text-gray-500 border-transparent hover:text-gray-700'
            }`}
          >
            Alertas de Productos
          </button>
        </div>
        
        {/* Search bar - Only in list tab */}
        {activeTab === 'list' && (
          <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
            <div className="relative flex-1">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                {isFetching && searchTerm ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent"></div>
                ) : (
                  <Search className="h-5 w-5 text-gray-400" />
                )}
              </div>
              <input
                ref={searchInputRef}
                autoFocus
                type="text"
                placeholder="Buscar por nombre, categoría, principio activo..."
                value={searchTerm}
                onChange={(e) => handleSearchChange(e.target.value)}
                className="block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 text-sm placeholder-gray-500 transition-all"
              />
              {searchTerm && (
                <button
                  onClick={() => handleSearchChange('')}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            <div className="flex items-center space-x-2 flex-wrap gap-y-2">
              {/* Filters button */}
              <button
                onClick={() => setIsFilterDrawerOpen(true)}
                className={`flex items-center space-x-2 px-3 py-2 rounded-lg border text-sm font-medium transition-all cursor-pointer ${
                  activeFiltersCount > 0
                    ? 'bg-emerald-50 border-emerald-300 text-emerald-700'
                    : 'bg-white border-gray-300 text-gray-700 hover:border-emerald-300'
                } focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500`}
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span>Filtros</span>
                {activeFiltersCount > 0 && (
                  <span className="ml-1 bg-emerald-600 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                    {activeFiltersCount}
                  </span>
                )}
              </button>

              {/* Products counter badge */}
              <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border border-emerald-200/50 rounded-lg px-4 py-2">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
                  <div className="text-sm">
                    {searchTerm ? (
                      <div className="flex items-center space-x-1">
                        <span className="font-semibold text-emerald-700">{paginationMeta?.total || products.length}</span>
                        <span className="text-gray-600">resultados encontrados</span>
                        <Search className="w-4 h-4 text-emerald-500 ml-1" />
                      </div>
                    ) : (
                      <div className="flex items-center space-x-1">
                        <span className="font-semibold text-emerald-700">{paginationMeta?.total || products.length}</span>
                        <span className="text-gray-600">productos registrados</span>
                        <Boxes className="w-4 h-4 text-emerald-500 ml-1" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Items per page selector */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-500 font-medium">Mostrar</span>
                <div className="relative" data-dropdown>
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className="bg-white border border-gray-300 rounded-lg px-3 py-2 pr-8 text-sm font-medium text-gray-700 hover:border-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 transition-all cursor-pointer flex items-center min-w-15"
                  >
                    {limit}
                  </button>
                  <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none">
                    <ChevronDown className={`w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`} />
                  </div>
                  {isDropdownOpen && (
                    <div className="absolute top-full left-0 mt-1 w-full bg-white border border-gray-300 rounded-lg shadow-lg z-50">
                      {[10, 20, 30, 50, 100].map((value) => (
                        <div
                          key={value}
                          onClick={() => {
                            handleLimitChange(value);
                            setIsDropdownOpen(false);
                          }}
                          className="px-3 py-2 text-sm font-medium text-emerald-700 hover:bg-emerald-50 cursor-pointer transition-colors first:rounded-t-lg last:rounded-b-lg"
                        >
                          {value}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              
              {/* Kardex button */}
              {hasPermission('Productos', 'ver') && (
                <button
                  onClick={() => navigate('/dashboard/kardex-lotes')}
                  title="Kardex de lotes"
                  className="bg-indigo-600 text-white w-10 h-10 rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center shadow-sm"
                >
                  <ArrowLeftRight className="w-5 h-5" />
                </button>
              )}

              {/* Export inventory button */}
              {hasPermission('Productos', 'exportar') && (
                <button
                  onClick={handleExportInventory}
                  title="Exportar inventario"
                  className="bg-blue-600 text-white w-10 h-10 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center shadow-sm"
                >
                  <Download className="w-5 h-5" />
                </button>
              )}

              {/* Bulk upload button */}
              {hasPermission('Productos', 'importar') && (
                <button
                  onClick={() => setIsBulkDrawerOpen(true)}
                  title="Importar productos"
                  className="bg-emerald-600 text-white w-10 h-10 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center shadow-sm"
                >
                  <Upload className="w-5 h-5" />
                </button>
              )}

              {/* Backup imágenes button */}
              {hasPermission('Productos', 'descargar') && (
                <button
                  onClick={async () => {
                    try {
                      const blob = await ProductsService.downloadImageBackup();
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `backup-imagenes-${new Date().toISOString().split('T')[0]}.zip`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      window.URL.revokeObjectURL(url);
                    } catch {
                      toast.error('Error al descargar backup de imágenes');
                    }
                  }}
                  title="Backup imágenes"
                  className="bg-purple-600 text-white w-10 h-10 rounded-lg hover:bg-purple-700 transition-colors flex items-center justify-center shadow-sm"
                >
                  <Image className="w-5 h-5" />
                </button>
              )}
              
              {/* Create product button */}
              {hasPermission('Productos', 'crear') && (
                <button
                  onClick={() => { openCreate(); }}
                  title="Nuevo producto"
                  className="bg-emerald-600 text-white w-10 h-10 rounded-lg hover:bg-emerald-700 transition-colors flex items-center justify-center shadow-sm"
                >
                  <Plus className="w-5 h-5" />
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Tab content */}
      {activeTab === 'list' ? (
        <>
          {/* Products table */}
          <div className="relative flex-1 overflow-auto">
            <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Producto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Vencimiento</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Categoría</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Ubicación</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Precio Venta</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Stock</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Creado por</th>
                    {isSuperAdmin() && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Margen</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Ganancia Total</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider bg-gray-50">Valor Venta</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {products.length === 0 ? (
                    <tr>
                      <td colSpan={isSuperAdmin() ? 10 : 7} className="px-6 py-12 text-center">
                        <div className="flex flex-col items-center">
                          <Package className="w-12 h-12 text-gray-300 mb-3" />
                          <p className="text-gray-500">No hay productos registrados</p>
                          <p className="text-gray-400 text-sm">Los productos aparecerán aquí.</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    products.map((product: Product) => {
                      const principal = product.presentaciones?.find(p => p.esPrincipal) || product.presentaciones?.[0];
                      const precio = principal?.precio || 0;
                      const precioCompra = principal?.precioCompra || 0;
                      const stock = product.stockBase || 0;
                      const stockStatus = getStockStatus(stock);
                      const margenPrincipal = precioCompra > 0 ? ((precio - precioCompra) / precioCompra) * 100 : 0;
                      const gananciaPrincipal = precioCompra > 0 ? (precio - precioCompra) * Math.floor(stock / (principal?.factor || 1)) : 0;
                      const totalVentaPrincipal = precio * Math.floor(stock / (principal?.factor || 1));
                      const otrasPresentaciones = product.presentaciones?.filter(p => p.id !== principal?.id && p.activo) || [];
                      const diasVenc = getDiasRestantesVencimiento(product);
                      const vencStatus = getVencimientoStatus(diasVenc);
                      const rowBgClass = stock === 0 ? 'bg-red-50/80' : diasVenc === null ? '' : diasVenc <= 7 ? 'bg-red-50/60' : diasVenc <= 30 ? 'bg-amber-50/60' : diasVenc <= 90 ? 'bg-blue-50/40' : '';
                      return (
                        <tr key={product.id} onClick={() => handleProductClick(product)} className={`hover:bg-gray-100 transition-colors cursor-pointer group ${rowBgClass}`}>
                          <td className="px-4 py-4">
                            <div className="flex items-center gap-3">
                              {getFirstProductImage(product.imagenes) ? (
                                <img src={getFirstProductImage(product.imagenes)} alt={product.nombre} className="w-24 h-24 rounded-2xl object-cover shadow-md" />
                              ) : (
                                <div className="w-24 h-24 rounded-2xl bg-gray-100 flex items-center justify-center">
                                  <Package className="w-12 h-12 text-gray-400" />
                                </div>
                              )}
                              <div>
                                <p className="font-medium text-gray-900 text-sm">{capitalizeProductName(product.nombre)}{product.variante && <span className="text-emerald-600"> ({product.variante})</span>}</p>
                                {product.requiereReceta && (
                                  <span className="text-xs text-amber-600 flex items-center gap-1">
                                    <AlertCircle className="w-3 h-3" /> Receta
                                  </span>
                                )}
                                {product.registroInvima && (
                                  <p className="text-xs text-gray-400">Invima: {product.registroInvima}</p>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {(() => {
                              const fechaStr = getProximaFechaVencimiento(product);
                              return (
                                <div className="flex flex-col gap-1">
                                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${vencStatus.bg} ${vencStatus.text} ${vencStatus.border}`}>
                                    {vencStatus.icon === 'alert' && <AlertTriangle className="w-3 h-3" />}
                                    {vencStatus.icon === 'clock' && <CalendarClock className="w-3 h-3" />}
                                    {vencStatus.icon === 'calendar' && <Calendar className="w-3 h-3" />}
                                    {vencStatus.label}
                                  </span>
                                  {fechaStr && (
                                    <span className="text-[10px] text-gray-400">
                                      {new Date(fechaStr).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}
                                    </span>
                                  )}
                                </div>
                              );
                            })()}
                          </td>
                          <td className="px-4 py-4">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                              {capitalizeProductName(product.categoria) || 'Sin categoría'}
                            </span>
                          </td>
                          <td className="px-4 py-4">
                            {product.ubicacion ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-700">
                                {product.ubicacion}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-1">
                              {/* Presentación principal */}
                              <div className="flex items-center justify-between bg-emerald-50 rounded px-2 py-1">
                                <span className="text-xs font-medium text-emerald-700">{principal?.nombre}</span>
                                <span className="text-sm font-bold text-gray-900">{formatPrice(precio)}</span>
                              </div>
                              {isSuperAdmin() && precioCompra > 0 && (
                                <span className="text-[10px] text-gray-400 px-2">Compra: {formatPrice(precioCompra)}</span>
                              )}
                              {/* Otras presentaciones */}
                              {otrasPresentaciones.map(p => (
                                <div key={p.id} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1 mt-0.5">
                                  <span className="text-xs font-medium text-gray-600">{p.nombre}</span>
                                  <span className="text-sm font-bold text-gray-900">{formatPrice(p.precio)}</span>
                                </div>
                              ))}
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            <div className="flex flex-col gap-0.5">
                              <span className={`text-sm font-medium ${stockStatus.color === 'red' ? 'text-red-600' : stockStatus.color === 'amber' ? 'text-amber-600' : 'text-emerald-600'}`}>
                                {Math.floor(stock / (principal?.factor || 1))} {principal?.nombre}
                              </span>
                              {otrasPresentaciones.map(p => (
                                <span key={p.id} className="text-xs text-gray-500">
                                  {Math.floor(stock / p.factor)} {p.nombre}
                                </span>
                              ))}
                              <span className="text-[10px] text-gray-400">({stock} un. totales)</span>
                            </div>
                          </td>
                          <td className="px-4 py-4">
                            {product.creadoPor ? (
                              <div className="flex items-center gap-2">
                                <div className="w-6 h-6 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center text-xs font-bold">
                                  {product.creadoPor.nombreCompleto.charAt(0).toUpperCase()}
                                </div>
                                <span className="text-sm text-gray-700">{product.creadoPor.nombreCompleto}</span>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </td>
                          {isSuperAdmin() && (
                            <>
                              <td className="px-4 py-4">
                                {precioCompra > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    <span className={`text-sm font-semibold ${margenPrincipal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {margenPrincipal.toFixed(0)}% <span className="text-[10px] font-normal text-gray-400">({principal?.nombre})</span>
                                    </span>
                                    {otrasPresentaciones.map(p => {
                                      const m = p.precioCompra && p.precioCompra > 0 ? ((p.precio - p.precioCompra) / p.precioCompra) * 100 : 0;
                                      return (
                                        <span key={p.id} className={`text-xs font-medium ${m >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {m.toFixed(0)}% <span className="text-[10px] font-normal text-gray-400">({p.nombre})</span>
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                              </td>
                              <td className="px-4 py-4">
                                {precioCompra > 0 ? (
                                  <div className="flex flex-col gap-0.5">
                                    <span className={`text-sm font-medium ${gananciaPrincipal >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                      {formatPrice(gananciaPrincipal)} <span className="text-[10px] font-normal text-gray-400">({principal?.nombre})</span>
                                    </span>
                                    {otrasPresentaciones.map(p => {
                                      const stockPres = Math.floor(stock / p.factor);
                                      const g = p.precioCompra && p.precioCompra > 0 ? (p.precio - p.precioCompra) * stockPres : 0;
                                      return (
                                        <span key={p.id} className={`text-xs font-medium ${g >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                          {formatPrice(g)} <span className="text-[10px] font-normal text-gray-400">({p.nombre})</span>
                                        </span>
                                      );
                                    })}
                                  </div>
                                ) : (
                                  <span className="text-gray-400 text-sm">-</span>
                                )}
                                <div className="opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-2 mt-1">
                                  <Eye className="w-4 h-4 text-gray-400" />
                                  {hasPermission('Productos', 'crear') && isSuperAdmin() && (
                                    <button
                                      onClick={(e) => { e.stopPropagation(); handleAddLote(product); }}
                                      className="text-emerald-600 hover:text-emerald-700 text-xs font-medium flex items-center gap-0.5"
                                      title="Registrar ingreso"
                                    >
                                      <Package className="w-3.5 h-3.5" /> +Ingreso
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="px-4 py-4">
                                <div className="flex flex-col gap-0.5">
                                  <span className="text-sm font-medium text-gray-900">
                                    {formatPrice(totalVentaPrincipal)} <span className="text-[10px] font-normal text-gray-400">({principal?.nombre})</span>
                                  </span>
                                  {otrasPresentaciones.map(p => {
                                    const stockPres = Math.floor(stock / p.factor);
                                    const totalVenta = p.precio * stockPres;
                                    return (
                                      <span key={p.id} className="text-xs text-gray-500">
                                        {formatPrice(totalVenta)} <span className="text-[10px] font-normal text-gray-400">({p.nombre})</span>
                                      </span>
                                    );
                                  })}
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            
            {/* Loading indicator — solo spinner sutil en el encabezado */}
            {isFetching && products.length > 0 && (
              <div className="absolute top-2 right-2 z-10">
                <div className="bg-white rounded-full shadow-md border border-gray-100 p-2 flex items-center space-x-2">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-emerald-600 border-t-transparent"></div>
                  <span className="text-xs font-medium text-gray-600">Buscando...</span>
                </div>
              </div>
            )}
          </div>

          {/* Pagination */}
          {paginationMeta && paginationMeta.total > 0 && (
            <div className="px-6 py-6 border-t border-gray-100 bg-gray-50/30">
              <div className="flex items-center justify-between">
                {/* Results info */}
                <div className="flex items-center space-x-6">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium text-gray-900">
                      {((paginationMeta.page - 1) * paginationMeta.limit) + 1}
                    </span>
                    <span className="mx-1">–</span>
                    <span className="font-medium text-gray-900">
                      {Math.min(paginationMeta.page * paginationMeta.limit, paginationMeta.total)}
                    </span>
                    <span className="mx-2">de</span>
                    <span className="font-semibold text-gray-900">{paginationMeta.total}</span>
                    <span className="ml-1">resultados</span>
                  </div>
                  
                  <div className="text-sm text-gray-500">
                    Página <span className="font-medium text-gray-900">{paginationMeta.page}</span> de <span className="font-medium text-gray-900">{paginationMeta.totalPages}</span>
                  </div>
                </div>
                
                {/* Pagination controls */}
                <div className="flex items-center space-x-1">
                  <button
                    onClick={() => handlePageChange(1)}
                    disabled={!paginationMeta.hasPreviousPage}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      paginationMeta.hasPreviousPage
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-300 cursor-not-allowed'
                    }`}
                    title="Primera página"
                  >
                    <ChevronsLeft className="w-4 h-4" />
                  </button>

                  <button
                    onClick={handlePreviousPage}
                    disabled={!paginationMeta.hasPreviousPage}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      paginationMeta.hasPreviousPage
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-300 cursor-not-allowed'
                    }`}
                    title="Página anterior"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </button>

                  <div className="flex items-center space-x-1 mx-2">
                    {(() => {
                      const pages = [];
                      const maxVisiblePages = 3;
                      let start = Math.max(1, paginationMeta.page - Math.floor(maxVisiblePages / 2));
                      let end = Math.min(paginationMeta.totalPages, start + maxVisiblePages - 1);
                      
                      start = Math.max(1, end - maxVisiblePages + 1);

                      if (start > 1) {
                        pages.push(
                          <button
                            key="1"
                            onClick={() => handlePageChange(1)}
                            className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                          >
                            1
                          </button>
                        );
                        if (start > 2) {
                          pages.push(
                            <span key="ellipsis-start" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                          );
                        }
                      }

                      for (let i = start; i <= end; i++) {
                        pages.push(
                          <button
                            key={i}
                            onClick={() => handlePageChange(i)}
                            className={`w-9 h-9 flex items-center justify-center text-sm font-medium rounded-lg transition-all ${
                              i === paginationMeta.page
                                ? 'bg-emerald-600 text-white shadow-sm'
                                : 'text-gray-700 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                          >
                            {i}
                          </button>
                        );
                      }

                      if (end < paginationMeta.totalPages) {
                        if (end < paginationMeta.totalPages - 1) {
                          pages.push(
                            <span key="ellipsis-end" className="w-9 h-9 flex items-center justify-center text-gray-400">⋯</span>
                          );
                        }
                        pages.push(
                          <button
                            key={paginationMeta.totalPages}
                            onClick={() => handlePageChange(paginationMeta.totalPages)}
                            className="w-9 h-9 flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all"
                          >
                            {paginationMeta.totalPages}
                          </button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <button
                    onClick={handleNextPage}
                    disabled={!paginationMeta.hasNextPage}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      paginationMeta.hasNextPage
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-300 cursor-not-allowed'
                    }`}
                    title="Página siguiente"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </button>

                  <button
                    onClick={() => handlePageChange(paginationMeta.totalPages)}
                    disabled={!paginationMeta.hasNextPage}
                    className={`w-9 h-9 flex items-center justify-center rounded-lg transition-all ${
                      paginationMeta.hasNextPage
                        ? 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                        : 'text-gray-300 cursor-not-allowed'
                    }`}
                    title="Última página"
                  >
                    <ChevronsRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      ) : (
        /* Stats Dashboard */
        <div className="p-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <div className="bg-gradient-to-br from-emerald-50 to-teal-50 p-5 rounded-xl border border-emerald-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Productos</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stats.total}</p>
                </div>
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <Boxes className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-rose-50 p-5 rounded-xl border border-red-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Bajo Stock</p>
                  <p className="text-2xl font-bold text-red-600 mt-1">{stats.lowStock}</p>
                </div>
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <AlertTriangle className="w-6 h-6 text-red-600" />
                </div>
              </div>
            </div>

            {isSuperAdmin() && (
              <>
                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 p-5 rounded-xl border border-indigo-100">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500">Valor Total Inventario</p>
                      <p className="text-2xl font-bold text-indigo-600 mt-1">{formatPrice(stats.totalValue)}</p>
                    </div>
                    <div className="p-3 bg-white rounded-xl shadow-sm">
                      <TrendingUp className="w-6 h-6 text-indigo-600" />
                    </div>
                  </div>
                </div>
                {Object.entries(stats.valueByPresentation).map(([nombre, valor]) => (
                  <div key={nombre} className="bg-gradient-to-br from-blue-50 to-cyan-50 p-5 rounded-xl border border-blue-100">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500">Valor {nombre}</p>
                        <p className="text-2xl font-bold text-blue-600 mt-1">{formatPrice(valor)}</p>
                      </div>
                      <div className="p-3 bg-white rounded-xl shadow-sm">
                        <TrendingUp className="w-6 h-6 text-blue-600" />
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 p-5 rounded-xl border border-purple-100">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Agotados</p>
                  <p className="text-2xl font-bold text-purple-600 mt-1">{stats.outOfStock}</p>
                </div>
                <div className="p-3 bg-white rounded-xl shadow-sm">
                  <ArrowUpDown className="w-6 h-6 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Alertas del Dashboard */}
          <div className="space-y-6">
            {/* Productos Agotados */}
            {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) === 0).length > 0 && (
              <div className="bg-white border-l-4 border-red-500 rounded-r-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                  <div className="p-1.5 bg-red-100 rounded-lg">
                    <PackageX className="w-5 h-5 text-red-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800">Productos Agotados</h3>
                  <span className="ml-auto bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) === 0).length}
                  </span>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) === 0).slice(0, showAllOutOfStock ? undefined : 6).map((product: Product) => (
                      <div 
                        key={product.id} 
                        onClick={() => handleProductClick(product)}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        <Package className="w-4 h-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{capitalizeProductName(product.nombre)}{product.variante && <span className="text-emerald-600"> ({product.variante})</span>}</p>
                          <p className="text-xs text-gray-500">{capitalizeProductName(product.categoria)}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    ))}
                    {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) === 0).length > 6 && (
                      <button
                        onClick={() => setShowAllOutOfStock(!showAllOutOfStock)}
                        className="flex items-center justify-center text-red-600 text-sm font-medium hover:underline cursor-pointer"
                      >
                        {showAllOutOfStock ? 'Ver menos' : `+${allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) === 0).length - 6} más`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Bajo Inventario */}
            {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) > 0 && (p.presentaciones?.[0]?.stock || 0) <= 5).length > 0 && (
              <div className="bg-white border-l-4 border-amber-500 rounded-r-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                  <div className="p-1.5 bg-amber-100 rounded-lg">
                    <PackageMinus className="w-5 h-5 text-amber-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800">Bajo Inventario</h3>
                  <span className="ml-auto bg-amber-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) > 0 && (p.presentaciones?.[0]?.stock || 0) <= 5).length}
                  </span>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) > 0 && (p.presentaciones?.[0]?.stock || 0) <= 5).slice(0, showAllLowStock ? undefined : 6).map((product: Product) => (
                      <div 
                        key={product.id} 
                        onClick={() => handleProductClick(product)}
                        className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        <Package className="w-4 h-4 text-gray-400" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 text-sm truncate">{capitalizeProductName(product.nombre)}{product.variante && <span className="text-emerald-600"> ({product.variante})</span>}</p>
                          <p className="text-xs text-gray-500">{product.presentaciones?.[0]?.stock || 0} unidades</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-gray-400" />
                      </div>
                    ))}
                    {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) > 0 && (p.presentaciones?.[0]?.stock || 0) <= 5).length > 6 && (
                      <button
                        onClick={() => setShowAllLowStock(!showAllLowStock)}
                        className="flex items-center justify-center text-amber-600 text-sm font-medium hover:underline cursor-pointer"
                      >
                        {showAllLowStock ? 'Ver menos' : `+${allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) > 0 && (p.presentaciones?.[0]?.stock || 0) <= 5).length - 6} más`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Próximos a Vencer */}
            {getProductosProximosAVencer(allProducts, diasVencimientoFiltro).length > 0 && (
              <div className="bg-white border-l-4 border-orange-500 rounded-r-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                  <div className="p-1.5 bg-orange-100 rounded-lg">
                    <CalendarClock className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800">Próximos a Vencer</h3>
                  <select
                    value={diasVencimientoFiltro}
                    onChange={(e) => { setDiasVencimientoFiltro(Number(e.target.value)); setShowAllExpiring(false); }}
                    className="ml-2 text-xs border border-gray-300 rounded-lg px-2 py-1 bg-white focus:border-gray-500"
                  >
                    <option value={30}>30 días</option>
                    <option value={60}>2 meses</option>
                    <option value={90}>3 meses</option>
                    <option value={120}>4 meses</option>
                    <option value={180}>6 meses</option>
                    <option value={365}>1 año</option>
                  </select>
                  <span className="ml-auto bg-orange-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {getProductosProximosAVencer(allProducts, diasVencimientoFiltro).length}
                  </span>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {getProductosProximosAVencer(allProducts, diasVencimientoFiltro).slice(0, showAllExpiring ? undefined : 6).map((product: Product) => {
                      const diasRestantes = Math.ceil((new Date(getProximaFechaVencimiento(product)!).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
                      return (
                        <div
                          key={product.id}
                          onClick={() => handleProductClick(product)}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <Calendar className="w-4 h-4 text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{capitalizeProductName(product.nombre)}{product.variante && <span className="text-emerald-600"> ({product.variante})</span>}</p>
                            <p className={`text-xs ${diasRestantes <= 7 ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                              {diasRestantes === 0 ? 'Vence hoy' : `Vence en ${diasRestantes} días`}
                            </p>
                          </div>
                          <ChevronRight className="w-4 h-4 text-gray-400" />
                        </div>
                      );
                    })}
                    {getProductosProximosAVencer(allProducts, diasVencimientoFiltro).length > 6 && (
                      <button
                        onClick={() => setShowAllExpiring(!showAllExpiring)}
                        className="flex items-center justify-center text-orange-600 text-sm font-medium hover:underline cursor-pointer"
                      >
                        {showAllExpiring ? 'Ver menos' : `+${getProductosProximosAVencer(allProducts, diasVencimientoFiltro).length - 6} más`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Margen de Ganancia Bajo (≤20%) */}
            {!isVendedor() && allProducts.filter((p: Product) => {
              if (!p.presentaciones?.[0]?.precioCompra || p.presentaciones?.[0]?.precioCompra <= 0) return false;
              const margen = ((p.presentaciones?.[0]?.precio || 0 - p.presentaciones?.[0]?.precioCompra) / p.presentaciones?.[0]?.precioCompra) * 100;
              return margen <= 20;
            }).length > 0 && (
              <div className="bg-white border-l-4 border-blue-500 rounded-r-xl shadow-sm overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-3">
                  <div className="p-1.5 bg-blue-100 rounded-lg">
                    <TrendingDown className="w-5 h-5 text-blue-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800">Margen de Ganancia Bajo (≤20%)</h3>
                  <span className="ml-auto bg-blue-600 text-white text-xs px-2 py-0.5 rounded-full">
                    {allProducts.filter((p: Product) => {
                      if (!p.presentaciones?.[0]?.precioCompra || p.presentaciones?.[0]?.precioCompra <= 0) return false;
                      const margen = ((p.presentaciones?.[0]?.precio || 0) - (p.presentaciones?.[0]?.precioCompra || 0)) / (p.presentaciones?.[0]?.precioCompra || 1) * 100;
                      return margen <= 20;
                    }).length}
                  </span>
                </div>
                <div className="p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {allProducts.filter((p: Product) => {
                      const pc = p.presentaciones?.[0]?.precioCompra || 0;
                      if (!pc || pc <= 0) return false;
                      const margen = ((p.presentaciones?.[0]?.precio || 0) - pc) / pc * 100;
                      return margen <= 20;
                    }).slice(0, showAllLowMargin ? undefined : 6).map((product: Product) => {
                      const precio = product.presentaciones?.[0]?.precio || 0;
                      const pc = product.presentaciones?.[0]?.precioCompra || 0;
                      const margen = pc ? ((precio - pc) / pc) * 100 : 0;
                      return (
                        <div 
                          key={product.id} 
                          onClick={() => handleProductClick(product)}
                          className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <TrendingDown className="w-4 h-4 text-gray-400" />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{capitalizeProductName(product.nombre)}{product.variante && <span className="text-emerald-600"> ({product.variante})</span>}</p>
                            <p className="text-xs text-gray-500">
                              {formatPrice(product.presentaciones?.[0]?.precioCompra!)} → {formatPrice(product.presentaciones?.[0]?.precio || 0)}
                            </p>
                          </div>
                          <span className={`text-xs font-semibold ${margen <= 10 ? 'text-red-600' : 'text-blue-600'}`}>
                            {margen.toFixed(1)}%
                          </span>
                        </div>
                      );
                    })}
                    {allProducts.filter((p: Product) => {
                      if (!p.presentaciones?.[0]?.precioCompra || p.presentaciones?.[0]?.precioCompra <= 0) return false;
                      const margen = ((p.presentaciones?.[0]?.precio || 0 - p.presentaciones?.[0]?.precioCompra) / p.presentaciones?.[0]?.precioCompra) * 100;
                      return margen <= 20;
                    }).length > 6 && (
                      <button
                        onClick={() => setShowAllLowMargin(!showAllLowMargin)}
                        className="flex items-center justify-center text-blue-600 text-sm font-medium hover:underline cursor-pointer"
                      >
                        {showAllLowMargin ? 'Ver menos' : `+${allProducts.filter((p: Product) => {
                          if (!p.presentaciones?.[0]?.precioCompra || p.presentaciones?.[0]?.precioCompra <= 0) return false;
                          const margen = ((p.presentaciones?.[0]?.precio || 0 - p.presentaciones?.[0]?.precioCompra) / p.presentaciones?.[0]?.precioCompra) * 100;
                          return margen <= 20;
                        }).length - 6} más`}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Mensaje cuando no hay alertas */}
            {allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) === 0).length === 0 &&
             allProducts.filter((p: Product) => (p.presentaciones?.[0]?.stock || 0) > 0 && (p.presentaciones?.[0]?.stock || 0) <= 5).length === 0 &&
             allProducts.filter((p: Product) => {
               const fechaVenc = getProximaFechaVencimiento(p);
               if (!fechaVenc) return false;
               const dias = Math.ceil((new Date(fechaVenc).getTime() - new Date().getTime()) / (1000 * 60 * 60 * 24));
               return dias >= 0 && dias <= 30;
             }).length === 0 &&
             allProducts.filter((p: Product) => {
               if (!p.presentaciones?.[0]?.precioCompra || p.presentaciones?.[0]?.precioCompra <= 0) return false;
               const margen = ((p.presentaciones?.[0]?.precio || 0 - p.presentaciones?.[0]?.precioCompra) / p.presentaciones?.[0]?.precioCompra) * 100;
               return margen <= 20;
             }).length === 0 && (
              <div className="bg-white border border-gray-200 rounded-xl p-8 text-center shadow-sm">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <ShieldAlert className="w-8 h-8 text-gray-600" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Sin Alertas</h3>
                <p className="text-gray-500">No hay alertas pendientes. El inventario está en buen estado.</p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Drawers */}
      <ProductDetailsDrawer
        isOpen={isDetailsDrawerOpen}
        product={selectedProduct}
        onClose={handleCloseDetails}
        onEdit={handleEdit}
        onAddLote={handleAddLote}
        onDeleteSuccess={() => refetch()}
        onBaja={handleOpenBajaModal}
        onReactivarSuccess={() => refetch()}
      />

      <AddLoteDrawer
        isOpen={isLoteDrawerOpen}
        onClose={handleCloseLoteDrawer}
        onSuccess={() => refetch()}
        product={productForLote}
      />

      <BulkUploadDrawer
        isOpen={isBulkDrawerOpen}
        onClose={() => setIsBulkDrawerOpen(false)}
        onSuccess={() => refetch()}
      />

      {/* Modal Dar de Baja */}
      {isBajaModalOpen && bajaProducto && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-red-50 to-orange-50">
              <div className="flex items-center gap-2">
                <Trash2 className="w-5 h-5 text-red-600" />
                <h3 className="text-lg font-semibold text-gray-900">Dar de Baja</h3>
              </div>
              <button
                onClick={() => { setIsBajaModalOpen(false); setBajaProducto(null); }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 py-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <span className="font-semibold">Producto:</span> {capitalizeProductName(bajaProducto.nombre)}
                </p>
                <p className="text-sm text-amber-700 mt-1">
                  Stock actual: <span className="font-semibold">{bajaProducto.stockBase || 0}</span> unidades
                </p>
              </div>

              {/* Selector de lote */}
              {bajaProducto.lotes && bajaProducto.lotes.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Lote a dar de baja</label>
                  <Listbox
                    value={bajaLoteId}
                    onChange={(loteId) => {
                      setBajaLoteId(loteId);
                      if (loteId) {
                        const lote = bajaProducto.lotes!.find(l => l.id === loteId);
                        if (lote) {
                          setBajaCantidad(lote.cantidad.toString());
                          setBajaPresentacionId(lote.presentacionId);
                        }
                      } else {
                        setBajaCantidad('');
                        setBajaPresentacionId('');
                      }
                    }}
                  >
                    <div className="relative">
                      <ListboxButton className="relative w-full cursor-pointer rounded-lg border border-gray-200 bg-white py-2.5 pl-3 pr-10 text-left text-sm text-gray-900 hover:border-red-300 focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all">
                        <span className="block truncate">
                          {(() => {
                            if (!bajaLoteId) return 'Baja general (todo el producto)';
                            const lote = bajaProducto.lotes!.find(l => l.id === bajaLoteId);
                            if (!lote) return 'Baja general (todo el producto)';
                            const fv = lote.fechaVencimiento ? new Date(lote.fechaVencimiento) : null;
                            const vencido = fv ? fv < new Date(new Date().setHours(0,0,0,0)) : false;
                            return `${lote.numeroLote} — ${lote.cantidad} unidades${fv ? ` (vence: ${fv.toLocaleDateString('es-CO')}${vencido ? ' ⚠ VENCIDO' : ''})` : ''}`;
                          })()}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                          <ChevronDown className="h-4 w-4 text-gray-400" aria-hidden="true" />
                        </span>
                      </ListboxButton>
                      <ListboxOptions
                        transition
                        className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-lg ring-1 ring-black/5 focus:outline-none data-[closed]:scale-95 data-[closed]:opacity-0 data-[enter]:duration-100 data-[leave]:duration-75 data-[enter]:ease-out data-[leave]:ease-in"
                      >
                        <ListboxOption
                          value=""
                          className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-red-50 data-[focus]:text-red-900 transition-colors"
                        >
                          <span className="block truncate">Baja general (todo el producto)</span>
                          <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-red-600 opacity-0 group-data-[selected]:opacity-100">
                            <CheckCircle className="h-4 w-4" />
                          </span>
                        </ListboxOption>
                        {(() => {
                          const hoy = new Date();
                          hoy.setHours(0, 0, 0, 0);
                          return bajaProducto.lotes!
                            .slice()
                            .sort((a, b) => {
                              if (bajaMotivo === 'VENCIMIENTO') {
                                const aVencido = a.fechaVencimiento ? new Date(a.fechaVencimiento) < hoy : false;
                                const bVencido = b.fechaVencimiento ? new Date(b.fechaVencimiento) < hoy : false;
                                if (aVencido && !bVencido) return -1;
                                if (!aVencido && bVencido) return 1;
                              }
                              return 0;
                            })
                            .map(lote => {
                              const fv = lote.fechaVencimiento ? new Date(lote.fechaVencimiento) : null;
                              const vencido = fv ? fv < hoy : false;
                              return (
                                <ListboxOption
                                  key={lote.id}
                                  value={lote.id}
                                  className="group relative cursor-pointer select-none py-2 pl-3 pr-9 text-gray-900 data-[focus]:bg-red-50 data-[focus]:text-red-900 transition-colors"
                                >
                                  <span className="block truncate">
                                    {lote.numeroLote} — {lote.cantidad} unidades
                                    {fv && (
                                      <span className={vencido ? 'text-red-600 font-semibold ml-1' : 'text-gray-500 ml-1'}>
                                        (vence: {fv.toLocaleDateString('es-CO')}{vencido ? ' ⚠ VENCIDO' : ''})
                                      </span>
                                    )}
                                  </span>
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-red-600 opacity-0 group-data-[selected]:opacity-100">
                                    <CheckCircle className="h-4 w-4" />
                                  </span>
                                </ListboxOption>
                              );
                            });
                        })()}
                      </ListboxOptions>
                    </div>
                  </Listbox>
                  {bajaLoteId && (
                    <p className="text-xs text-gray-500 mt-1">
                      Al seleccionar un lote, solo se descuenta ese lote y el producto sigue activo si tiene otros lotes.
                    </p>
                  )}
                  {!bajaLoteId && (
                    <p className="text-xs text-amber-600 mt-1">
                      ⚠ La baja general desactivará el producto completo.
                    </p>
                  )}
                </div>
              )}

              {bajaProducto.presentaciones && bajaProducto.presentaciones.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Presentación</label>
                  <select
                    value={bajaPresentacionId}
                    onChange={(e) => setBajaPresentacionId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                  >
                    {bajaProducto.presentaciones.filter(p => p.activo).map((p) => (
                      <option key={p.id} value={p.id}>{p.nombre} (factor {p.factor})</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    La cantidad se interpreta en esta presentación.
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Motivo</label>
                <select
                  value={bajaMotivo}
                  onChange={(e) => {
                    const nuevoMotivo = e.target.value;
                    setBajaMotivo(nuevoMotivo);
                    if (nuevoMotivo !== 'VENCIMIENTO') {
                      // No filtrar lotes si cambia motivo; dejar selección actual
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                >
                  <option value="VENCIMIENTO">Vencimiento</option>
                  <option value="DAÑO">Daño</option>
                  <option value="PERDIDA">Pérdida</option>
                  <option value="ROBO">Robo</option>
                  <option value="OTRO">Otro</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Cantidad</label>
                <input
                  type="number"
                  min={1}
                  value={bajaCantidad}
                  onChange={(e) => setBajaCantidad(e.target.value)}
                  placeholder="Cantidad a dar de baja"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Observaciones</label>
                <textarea
                  value={bajaObservaciones}
                  onChange={(e) => setBajaObservaciones(e.target.value)}
                  placeholder="Observaciones opcionales..."
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 resize-none"
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
              <button
                onClick={() => { setIsBajaModalOpen(false); setBajaProducto(null); }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleBajaSubmit}
                disabled={isBajaSubmitting || !bajaCantidad || parseInt(bajaCantidad, 10) <= 0}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
              >
                {isBajaSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4" />
                    Confirmar Baja
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Filter Drawer */}
      {isFilterDrawerOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed top-20 left-0 right-0 bottom-0 bg-black/20 z-40 transition-opacity"
            onClick={() => setIsFilterDrawerOpen(false)}
          />
          {/* Drawer panel */}
          <div className="fixed top-20 right-0 bottom-0 w-[36rem] bg-white shadow-2xl z-50 transform transition-transform translate-x-0 flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                  <SlidersHorizontal className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Filtros</h3>
                  <p className="text-sm text-gray-500">Refina los productos mostrados</p>
                </div>
              </div>
              <button
                onClick={() => setIsFilterDrawerOpen(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Active filter chips */}
            {activeFiltersCount > 0 && (
              <div className="px-6 py-3 border-b border-gray-100">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-medium text-gray-500 uppercase tracking-wider">Filtros activos</span>
                  <button
                    onClick={() => {
                      setCreatorFilter('');
                      setUbicacionFilter('');
                      setEstadoFilter('todos');
                      setStockFilter('todos');
                      setUserSearchTerm('');
                      setUbicacionSearchTerm('');
                    }}
                    className="text-xs text-red-600 hover:text-red-700 font-medium"
                  >
                    Limpiar todos
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {creatorFilter && (
                    <span className="inline-flex items-center space-x-1 bg-emerald-50 text-emerald-700 text-xs font-medium px-2.5 py-1 rounded-full border border-emerald-200">
                      <Users className="w-3 h-3" />
                      <span>{(usersData as Array<{id: string; nombreCompleto: string}>)?.find((u) => u.id === creatorFilter)?.nombreCompleto || 'Usuario'}</span>
                      <button onClick={() => setCreatorFilter('')} className="ml-1 hover:text-emerald-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {ubicacionFilter && (
                    <span className="inline-flex items-center space-x-1 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full border border-blue-200">
                      <MapPin className="w-3 h-3" />
                      <span>{ubicacionFilter}</span>
                      <button onClick={() => setUbicacionFilter('')} className="ml-1 hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {estadoFilter !== 'todos' && (
                    <span className="inline-flex items-center space-x-1 bg-emerald-50 text-emerald-700 text-xs font-medium px-2.5 py-1 rounded-full border border-emerald-200">
                      <span className="capitalize">{estadoFilter === 'activo' ? 'Activos' : 'Inactivos'}</span>
                      <button onClick={() => setEstadoFilter('todos')} className="ml-1 hover:text-emerald-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                  {stockFilter !== 'todos' && (
                    <span className="inline-flex items-center space-x-1 bg-blue-50 text-blue-700 text-xs font-medium px-2.5 py-1 rounded-full border border-blue-200">
                      <span>
                        {stockFilter === 'agotado' ? 'Agotados' : stockFilter === 'bajo' ? 'Bajo stock' : 'Disponibles'}
                      </span>
                      <button onClick={() => setStockFilter('todos')} className="ml-1 hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}

            {/* Filter sections */}
            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {/* Estado filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado del producto</label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { key: 'todos', label: 'Todos' },
                    { key: 'activo', label: 'Activos' },
                    { key: 'inactivo', label: 'Inactivos' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setEstadoFilter(opt.key as any)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                        estadoFilter === opt.key
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Stock filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estado de stock</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { key: 'todos', label: 'Todo stock' },
                    { key: 'agotado', label: 'Agotados' },
                    { key: 'bajo', label: 'Bajo stock' },
                    { key: 'disponible', label: 'Disponibles' },
                  ].map((opt) => (
                    <button
                      key={opt.key}
                      onClick={() => setStockFilter(opt.key as any)}
                      className={`px-3 py-2 text-sm font-medium rounded-lg border transition-all ${
                        stockFilter === opt.key
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Creator filter - only for super admin */}
              {isSuperAdmin() && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Creado por</label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Buscar usuario..."
                      value={userSearchTerm}
                      onChange={(e) => setUserSearchTerm(e.target.value)}
                      className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                    />
                    {userSearchTerm && (
                      <button
                        onClick={() => setUserSearchTerm('')}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <div className="mt-2 space-y-1 max-h-52 overflow-y-auto border border-gray-100 rounded-lg p-1">
                    <button
                      onClick={() => setCreatorFilter('')}
                      className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 ${
                        !creatorFilter ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                      }`}
                    >
                      <Users className="w-4 h-4" />
                      <span>Todos los creadores</span>
                    </button>
                    {(usersData as Array<{id: string; nombreCompleto: string}> | undefined)
                      ?.filter((u) =>
                        u.nombreCompleto.toLowerCase().includes(userSearchTerm.toLowerCase())
                      )
                      .map((user) => (
                        <button
                          key={user.id}
                          onClick={() => setCreatorFilter(user.id)}
                          className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 ${
                            creatorFilter === user.id ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                          }`}
                        >
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            creatorFilter === user.id ? 'bg-emerald-200 text-emerald-800' : 'bg-gray-100 text-gray-600'
                          }`}>
                            {user.nombreCompleto.charAt(0).toUpperCase()}
                          </div>
                          <span>{user.nombreCompleto}</span>
                        </button>
                      ))}
                    {(usersData as Array<{id: string; nombreCompleto: string}> | undefined)?.filter((u) =>
                      u.nombreCompleto.toLowerCase().includes(userSearchTerm.toLowerCase())
                    ).length === 0 && (
                      <div className="px-3 py-3 text-sm text-gray-400 text-center">
                        No se encontraron usuarios
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Ubicación filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Ubicación</label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar ubicación..."
                    value={ubicacionSearchTerm}
                    onChange={(e) => setUbicacionSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500"
                  />
                  {ubicacionSearchTerm && (
                    <button
                      onClick={() => setUbicacionSearchTerm('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="mt-2 space-y-1 max-h-52 overflow-y-auto border border-gray-100 rounded-lg p-1">
                  <button
                    onClick={() => setUbicacionFilter('')}
                    className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 ${
                      !ubicacionFilter ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                    }`}
                  >
                    <MapPin className="w-4 h-4" />
                    <span>Todas las ubicaciones</span>
                  </button>
                  {ubicacionesUnicas
                    .filter((u) =>
                      u.toLowerCase().includes(ubicacionSearchTerm.toLowerCase())
                    )
                    .map((ubicacion) => (
                      <button
                        key={ubicacion}
                        onClick={() => setUbicacionFilter(ubicacion)}
                        className={`w-full text-left px-3 py-2 text-sm rounded-md transition-colors flex items-center space-x-2 ${
                          ubicacionFilter === ubicacion ? 'bg-emerald-50 text-emerald-700 font-medium' : 'text-gray-700 hover:bg-emerald-50 hover:text-emerald-700'
                        }`}
                      >
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          ubicacionFilter === ubicacion ? 'bg-emerald-200 text-emerald-800' : 'bg-gray-100 text-gray-600'
                        }`}>
                          {ubicacion.charAt(0).toUpperCase()}
                        </div>
                        <span>{ubicacion}</span>
                      </button>
                    ))}
                  {ubicacionesUnicas.filter((u) =>
                    u.toLowerCase().includes(ubicacionSearchTerm.toLowerCase())
                  ).length === 0 && (
                    <div className="px-3 py-3 text-sm text-gray-400 text-center">
                      No se encontraron ubicaciones
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-gray-100 flex justify-end gap-3 bg-gray-50">
              <button
                onClick={() => {
                  setCreatorFilter('');
                  setUbicacionFilter('');
                  setEstadoFilter('todos');
                  setStockFilter('todos');
                  setUserSearchTerm('');
                  setUbicacionSearchTerm('');
                }}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Limpiar todos
              </button>
              <button
                onClick={() => setIsFilterDrawerOpen(false)}
                className="px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition-colors"
              >
                Aplicar filtros
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
